'use client';

import { useState, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Globe } from 'lucide-react';
import { useTranslation } from '@/i18n/LanguageProvider';
import { locales, localeNames } from '@/i18n/config';
import { switchLocalePath } from '@/data/landingLocales';
import FlagIcon from '@/components/FlagIcon';

/**
 * Selector de idioma del header. El idioma vive en la URL, así que al
 * elegir un idioma se navega a la URL equivalente en ese idioma (recarga
 * completa para que el HTML llegue ya renderizado en el nuevo idioma).
 */
export default function HeaderLangSelector() {
  const { locale } = useTranslation();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goTo = (code: typeof locales[number]) => {
    setOpen(false);
    if (code === locale) return;
    // Fijar la preferencia explícita del usuario para que la autodetección
    // del middleware no la sobrescriba en la siguiente visita (importante al
    // elegir español, que no lleva prefijo de URL y, sin cookie, se vería
    // como una primera visita y podría volver a autodetectarse).
    document.cookie = `iterogo_lang=${code}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    window.location.href = switchLocalePath(pathname || '/', code);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Cambiar idioma"
      >
        <Globe className="w-4 h-4" />
        <span className="text-xs font-medium uppercase">{locale}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-80 overflow-y-auto z-50">
          {locales.map(code => (
            <button
              key={code}
              onClick={() => goTo(code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                locale === code ? 'bg-primary/5 text-primary font-medium' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <FlagIcon code={code} width={20} height={14} />
              <span>{localeNames[code]}</span>
              {locale === code && <span className="ml-auto text-primary text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
